// Derived chute topology (IMPLEMENTATION §2.1 topology.ts, §0.2 R8). Never saved or hashed: built lazily from
// the buildings and the occupancy index (topoOf) and dropped whenever a building is added, removed, moved or
// rotated (buildings.ts sets ctx.topo = null), so it is rebuilt on build changes and at most once per tick.
//   chain   a chute passes its item out through `dir` into the next cell. The next chute continues the chain
//           only if its back (`inDir` side) faces this chute; a misaligned chute, rock or a cycle ends the
//           chain with no sink (cycle-guarded by a step bound).
//   sink    the first non-chute along the out directions: a building (entered from `sinkSide`), or the floor
//           tile in front (a loose item may sit there: the tile then accepts nothing until it is free).
//   order   chute ids downstream-first: hops to the sink ascending, then id; sinkless chutes last.
//   reserved  per sink cell: the items in transit to it (every chute carry of the chains that end there).
//           Smart pull and every automatic insert count them (R8 reservations, rules/accept.ts).
import { cellIndex, stepCell } from './core/grid.ts';
import { sortedById } from './core/sort.ts';
import type { Dir, Int } from './core/types.ts';
import type { Ctx } from './ctx.ts';
import { findBuilding } from './entities.ts';
import { isFree, isOpen, occAt } from './occupancy.ts';
import type { ChuteB, ItemRef } from './state.ts';

export interface Topo {
  /** Per chute cell (cell index): the cell index of its chain sink, -1 = none. */
  sink: Int[];
  /** Per chute cell: the side of the sink building facing the chain's last chute (null for a floor sink). */
  sinkSide: (Dir | null)[];
  /** Per chute cell: chutes from this one to the sink, itself included (1 = next to the sink). */
  depth: Int[];
  /** Chute ids, downstream-first. */
  order: Int[];
  /** Per sink cell index: items in transit to it. */
  reserved: ItemRef[][];
}

/** The side of cell (ax, ay) that faces its 4-neighbour (bx, by). */
function sideFacing(ax: Int, ay: Int, bx: Int, by: Int): Dir {
  if (bx > ax) return 'E';
  if (bx < ax) return 'W';
  return by > ay ? 'S' : 'N';
}

function chuteAt(c: Ctx, x: Int, y: Int): ChuteB | null {
  const id = occAt(c.occ, x, y);
  if (id <= 0) return null;
  const b = findBuilding(c.s, id);
  return b !== null && b.type === 'chute' ? b : null;
}

/** True when chute `b` takes items from the cell (x, y) (its back faces it). */
export function chuteTakesFrom(b: ChuteB, x: Int, y: Int): boolean {
  const back = stepCell(b.x, b.y, b.inDir);
  return back.x === x && back.y === y;
}

export function buildTopo(c: Ctx): Topo {
  const o = c.occ;
  const n = o.cols * o.rows;
  const sink: Int[] = [];
  const sinkSide: (Dir | null)[] = [];
  const depth: Int[] = [];
  const reserved: ItemRef[][] = [];
  for (let i = 0; i < n; i++) {
    sink.push(-1);
    sinkSide.push(null);
    depth.push(0);
    reserved.push([]);
  }
  const chutes: ChuteB[] = [];
  for (const b of c.s.buildings) if (b.type === 'chute') chutes.push(b);
  for (const ch of chutes) {
    let cur: ChuteB = ch;
    let hops = 1;
    let target = -1;
    let side: Dir | null = null;
    while (hops <= n) {
      const nx = stepCell(cur.x, cur.y, cur.dir);
      if (!isOpen(o, nx.x, nx.y)) break;
      const id = occAt(o, nx.x, nx.y);
      const next = id > 0 ? findBuilding(c.s, id) : null;
      if (next !== null && next.type === 'chute') {
        if (!chuteTakesFrom(next, cur.x, cur.y)) break;
        cur = next;
        hops++;
        continue;
      }
      target = cellIndex(o.cols, nx.x, nx.y);
      side = next !== null ? sideFacing(nx.x, nx.y, cur.x, cur.y) : null;
      break;
    }
    const ci = cellIndex(o.cols, ch.x, ch.y);
    if (target >= 0) {
      sink[ci] = target;
      sinkSide[ci] = side;
      depth[ci] = hops;
    }
  }
  const order = sortedById(chutes, (b) => {
    const ci = cellIndex(o.cols, b.x, b.y);
    return (sink[ci] as Int) >= 0 ? (depth[ci] as Int) : n + 1;
  }).map((b) => b.id);
  for (const ch of chutes) {
    const s = sink[cellIndex(o.cols, ch.x, ch.y)] as Int;
    if (s >= 0 && ch.carry !== null) (reserved[s] as ItemRef[]).push(ch.carry);
  }
  return { sink, sinkSide, depth, order, reserved };
}

export function topoOf(c: Ctx): Topo {
  if (c.topo === null) c.topo = buildTopo(c);
  return c.topo;
}

/** The chain sink cell of a chute (-1 = none). */
export function sinkOf(c: Ctx, ch: ChuteB): Int {
  return topoOf(c).sink[cellIndex(c.occ.cols, ch.x, ch.y)] ?? -1;
}

/** Items in transit to the building / floor tile at (x, y). */
export function reservedAt(c: Ctx, x: Int, y: Int): readonly ItemRef[] {
  if (!isOpen(c.occ, x, y)) return [];
  return topoOf(c).reserved[cellIndex(c.occ.cols, x, y)] ?? [];
}

/**
 * A free tile that no chute chain is delivering onto. Automatic writers (deposit spawns, machine pushes,
 * overflow ejects) use it, so a floor sink reserved by a chain is never taken from under it; hand drops may
 * still take it (the chain then waits NO_ROOM until the player clears the tile).
 */
export function freeUnreserved(c: Ctx, x: Int, y: Int): boolean {
  return isFree(c.occ, x, y) && reservedAt(c, x, y).length === 0;
}

export function reserve(c: Ctx, sinkCell: Int, r: ItemRef): void {
  if (sinkCell >= 0) topoOf(c).reserved[sinkCell]?.push(r);
}

export function unreserve(c: Ctx, sinkCell: Int, id: Int): void {
  if (sinkCell < 0) return;
  const list = topoOf(c).reserved[sinkCell];
  if (!list) return;
  for (let i = 0; i < list.length; i++) {
    if ((list[i] as ItemRef).id === id) {
      list.splice(i, 1);
      return;
    }
  }
}

/** The chute a chain continues into from `ch`, or null (sink reached / chain end). */
export function nextChute(c: Ctx, ch: ChuteB): ChuteB | null {
  const nx = stepCell(ch.x, ch.y, ch.dir);
  if (!isOpen(c.occ, nx.x, nx.y)) return null;
  const next = chuteAt(c, nx.x, nx.y);
  return next !== null && chuteTakesFrom(next, ch.x, ch.y) ? next : null;
}
